/**
 * AI Scribe V2 — stick-to-bottom scroll
 *
 * Keeps a scroll container pinned to its newest content, but yields the moment
 * the user scrolls up so they can read earlier lines without the panel yanking
 * them back down. Exposes `isPinned` so callers can offer a "jump to latest".
 */

import { RefObject, useCallback, useRef, useState } from "react";
import { SCRIBE_TRANSCRIPT_STICK_THRESHOLD_PX } from "./constants";

export interface UseStickToBottomResult {
  /** Attach to the scrollable container. */
  scrollRef: RefObject<HTMLDivElement>;
  /** True while the view sits at (or within the threshold of) the bottom. */
  isPinned: boolean;
  /** Wire to the container's `onScroll`. */
  onScroll: () => void;
  /** Force the view to the newest content and re-pin. */
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  /** Scroll to the bottom only when the view was already pinned. */
  stickIfPinned: () => void;
}

export const useStickToBottom = (
  thresholdPx: number = SCRIBE_TRANSCRIPT_STICK_THRESHOLD_PX
): UseStickToBottomResult => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(true);
  /** Mirrors `isPinned` so `stickIfPinned` reads it without re-subscribing. */
  const isPinnedRef = useRef(true);

  const setPinned = useCallback((pinned: boolean) => {
    isPinnedRef.current = pinned;
    setIsPinned(pinned);
  }, []);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior });
      setPinned(true);
    },
    [setPinned]
  );

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setPinned(distance <= thresholdPx);
  }, [thresholdPx, setPinned]);

  const stickIfPinned = useCallback(() => {
    if (isPinnedRef.current) scrollToBottom();
  }, [scrollToBottom]);

  return { scrollRef, isPinned, onScroll, scrollToBottom, stickIfPinned };
};
